import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { apiFetch } from '../api';
import { STAGE_GROUPS } from '../auth/roles';
import { PageHeader, QuantityProgress, DeadlineBadge } from './ui';
import { formatDate } from '../utils/date';
import { formatCurrency } from '../utils/display';
import DetailFiles from './DetailFiles';

function DetailsByStage({ stageGroup }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const groupInfo = STAGE_GROUPS[stageGroup];
  const title = groupInfo ? `${groupInfo.icon} ${groupInfo.label}` : 'Детали';
  const subtitle = groupInfo ? `Детали на стадии «${groupInfo.label}»` : 'Детали по стадии';

  const loadDetails = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (stageGroup) params.set('stage_group', stageGroup);
    return apiFetch(`/api/details/?${params}`)
      .then((data) => {
        setDetails(data.results || data);
        setRefreshKey(Date.now());
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить детали');
        setLoading(false);
      });
  }, [stageGroup]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  // Фильтрация по поиску
  const filtered = details.filter((d) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.contract?.number || '').toLowerCase().includes(q) ||
      (d.contract?.product_name || '').toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-card-label">Всего деталей</div>
          <div className="stat-card-value">{filtered.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Общее количество</div>
          <div className="stat-card-value">
            {filtered.reduce((sum, d) => sum + Number(d.quantity), 0)}
          </div>
        </div>
      </div>

      <div className="search-box">
        <FaSearch className="app-text-muted" />
        <input
          placeholder="Поиск по названию детали, номеру договора или изделию"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="details-list">
        {filtered.length === 0 ? (
          <div className="empty-state">Нет деталей на этой стадии</div>
        ) : (
          filtered.map((detail) => (
            <DetailCard key={detail.id} detail={detail} refreshKey={refreshKey} navigate={navigate} />
          ))
        )}
      </div>
    </>
  );
}

function DetailCard({ detail, refreshKey, navigate }) {
  const totalPrice = Number(detail.quantity) * Number(detail.price_per_unit);
  const contractId = detail.contract;

  const goToContract = (e) => {
    e.stopPropagation();
    if (contractId) {
      navigate(`/contract/${contractId}/stages`);
    }
  };

  return (
    <div className="detail-card detail-card-full detail-card-with-bg">
      {detail.image && (
        <img
          src={detail.image}
          alt={detail.name}
          style={{
            maxWidth: '100%',
            maxHeight: '200px',
            objectFit: 'contain',
            display: 'block',
            marginBottom: '8px',
            borderRadius: '4px',
          }}
        />
      )}
      <div className="detail-card-body">
        <div className="detail-card-header">
          <span className="detail-card-name">{detail.name}</span>
          <span className="detail-card-quantity">× {formatQuantity(detail.quantity)}</span>
        </div>

        <div className="detail-card-badges">
          {detail.material && (
            <span className="app-badge app-badge-neutral">Материал: {detail.material}</span>
          )}
          {detail.blank_type && (
            <span className="app-badge app-badge-neutral">Заготовка: {detail.blank_type}</span>
          )}
          {detail.dimension_per_unit && (
            <span className="app-badge app-badge-neutral">1 шт: {detail.dimension_per_unit}</span>
          )}
          {detail.dimension_total && (
            <span className="app-badge app-badge-neutral">Всего: {detail.dimension_total}</span>
          )}
        </div>

        <div className="detail-card-details-grid">
          <div className="detail-card-field">
            <span className="card-label">Цена за ед.</span>
            <span className="card-value">{formatCurrency(detail.price_per_unit)}</span>
          </div>
          <div className="detail-card-field">
            <span className="card-label">Общая сумма</span>
            <span className="card-value">{formatCurrency(totalPrice)}</span>
          </div>
          <div className="detail-card-field">
            <span className="card-label">Статус</span>
            <span className="card-value">{detail.status || '—'}</span>
          </div>
          <div className="detail-card-field">
            <span className="card-label">Срок</span>
            <span className="card-value">{formatDate(detail.deadline)}</span>
          </div>
          {detail.description && (
            <div className="detail-card-field detail-card-description">
              <span className="card-label">Описание</span>
              <span className="card-value">{detail.description}</span>
            </div>
          )}
        </div>
        <div className="detail-card-actions" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            className="app-btn app-btn-sm app-btn-primary"
            onClick={goToContract}
            title="Перейти к договору"
          >
            <FaExternalLinkAlt /> Перейти к договору
          </button>
        </div>
      </div>
    </div>
  );
}

function formatQuantity(value) {
  if (value === null || value === undefined) return '0';
  const num = Number(value);
  if (Number.isInteger(num)) {
    return num.toString();
  }
  return num.toFixed(2);
}

export default DetailsByStage;